// Credit card confirmation dialog where the user enters in their
// credit card information and accepts the charges. Uses the Clover
// Ecommerce SDK (clover.js) to embed tokenizing iframes for the card
// number, expiry, CVV and postal code so the raw card data never
// touches our servers. The token returned by clover.createToken() is
// sent to our `creditCardPaymentProcess` cloud function which charges
// the card via Clover and tops up the user's F2PM wallet.
import React, { useRef, useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import DialogActions from "@material-ui/core/DialogActions";
import Typography from "@material-ui/core/Typography";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";

// Public keys come from `farming_marketplace/.env.local`:
//   REACT_APP_CLOVER_PAKMS_KEY       = the Ecommerce API "Public Token"
//                                      (a.k.a. apiAccessKey / PAKMS) issued
//                                      for the target merchant. This is NOT
//                                      the Clover App ID.
//   REACT_APP_CLOVER_MERCHANT_ID     = the merchant id the public token was
//                                      issued for.
//   REACT_APP_CLOVER_USE_PRODUCTION  = "true" to load checkout.clover.com,
//                                      anything else loads the sandbox SDK.
const CLOVER_PAKMS_KEY = process.env.REACT_APP_CLOVER_PAKMS_KEY || "";
const CLOVER_MERCHANT_ID = process.env.REACT_APP_CLOVER_MERCHANT_ID || "";
const CLOVER_USE_PRODUCTION =
  process.env.REACT_APP_CLOVER_USE_PRODUCTION === "true";

const CLOVER_SDK_URL = CLOVER_USE_PRODUCTION
  ? "https://checkout.clover.com/sdk.js"
  : "https://checkout.sandbox.dev.clover.com/sdk.js";

const CARD_MOUNT_IDS = {
  number: "f2pm-clover-card-number",
  date: "f2pm-clover-card-date",
  cvv: "f2pm-clover-card-cvv",
  postal: "f2pm-clover-card-postal",
};

/** Load Clover once; URL must match sandbox vs live PAKMS keys. */
function loadCloverSdkScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("No window"));
      return;
    }
    if (window.Clover) {
      resolve();
      return;
    }
    const existing = document.querySelector(`script[data-f2pm-clover-sdk]`);
    if (existing) {
      if (window.Clover) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Clover script failed")),
        { once: true },
      );
      return;
    }
    const script = document.createElement("script");
    script.src = CLOVER_SDK_URL;
    script.async = true;
    script.dataset.f2pmCloverSdk = CLOVER_USE_PRODUCTION ? "prod" : "sandbox";
    script.onload = () => resolve();
    script.onerror = () =>
      reject(
        new Error(
          `Failed to load Clover SDK (${CLOVER_SDK_URL}). Check network and REACT_APP_CLOVER_USE_PRODUCTION.`,
        ),
      );
    document.head.appendChild(script);
  });
}

// Shared iframe styles for the four card-input iframes. Clover applies these
// inside the iframe via their SDK so we get a consistent look with the rest
// of the dialog.
const cloverInputStyles = {
  body: {
    fontFamily: "Roboto, Arial, sans-serif",
    fontSize: "16px",
  },
  input: {
    fontSize: "16px",
    color: "#333",
  },
  "input::placeholder": {
    color: "#999",
  },
  ".invalid": {
    color: "#d32f2f",
  },
};

export default function CreditCardDialogConfirmation({
  open,
  amount,
  setLoadWalletOpen,
  onClose,
  userId,
  CloverCreditCardPercentage,
  CloverCreditCardFlatFee,
  setCommunityWallet,
  setBalance,
  toCents,
  placeOrderOnConfirm,
}) {
  // Clover mounts into divs identified by stable ids (see Clover docs — mount
  // uses CSS selectors). Wait until the dialog transition has finished so the
  // containers have real layout (MUI Modal/portal timing).
  const [dialogEntered, setDialogEntered] = useState(false);
  // Holds the live Clover instance so we can call createToken() on submit
  // and tear iframes down when the dialog closes.
  const cloverInstanceRef = useRef(null);

  const functionUrl =
    process.env.REACT_APP_CREDIT_CARD_FUNCTION_URL ||
    "https://us-central1-farm-to-table-1177f.cloudfunctions.net/creditCardPaymentProcess";

  // Total being charged to the card (load amount + Clover fees).
  const [amountPlusFee, setAmountPlusFee] = useState(amount);
  // Whether the network call to Clover is in flight.
  const [processing, setProcessing] = useState(false);
  // Any error message we want to surface to the user (tokenize failure,
  // backend rejection, etc.).
  const [errorMessage, setErrorMessage] = useState("");

  const handleBack = () => {
    setLoadWalletOpen(true);
    onClose(false);
  };

  useEffect(() => {
    if (open) {
      setErrorMessage("");
    }
  }, [open]);

  useEffect(() => {
    // Gross-up the customer charge so Clover's percentage cut still leaves
    // `amount` in the wallet. If we only added the markup
    // (amount * 1.024) Clover would then take 2.4% of THAT, leaving us short.
    // Solves:  gross - (gross * r) - flatFee = amount
    // =>       gross = (amount + flatFee) / (1 - r)
    const r = (CloverCreditCardPercentage || 0) / 100;
    const safeDenominator = 1 - r > 0 ? 1 - r : 1; // guard against bad config
    const grossUp =
      ((amount || 0) + (CloverCreditCardFlatFee || 0)) / safeDenominator;
    // Round UP to the nearest cent so a sub-cent rounding never leaves us
    // short of the fee Clover actually deducts.
    const amountPlusFeeTemp = Math.ceil(grossUp * 100) / 100;
    setAmountPlusFee(amountPlusFeeTemp);
  }, [amount, CloverCreditCardPercentage, CloverCreditCardFlatFee]);

  // Initialize Clover only when the dialog is visible and the enter transition
  // has finished (avoids mount failures in a hidden modal).
  useEffect(() => {
    let cancelled = false;

    if (!open || !dialogEntered) {
      return () => {
        const instance = cloverInstanceRef.current;
        if (instance) {
          try {
            instance.cardNumber.destroy?.();
            instance.cardDate.destroy?.();
            instance.cardCvv.destroy?.();
            instance.cardPostal.destroy?.();
          } catch (err) {
            console.warn("Clover element teardown failed:", err);
          }
          cloverInstanceRef.current = null;
        }
      };
    }

    async function initClover() {
      try {
        if (!CLOVER_PAKMS_KEY || !CLOVER_MERCHANT_ID) {
          throw new Error(
            "Missing REACT_APP_CLOVER_PAKMS_KEY or REACT_APP_CLOVER_MERCHANT_ID",
          );
        }

        await loadCloverSdkScript();
        if (cancelled) return;

        if (!window.Clover) {
          console.error("Clover SDK not loaded");
          setErrorMessage(
            "Payment form failed to load. Please refresh and try again.",
          );
          return;
        }

        const mountSelectors = [
          `#${CARD_MOUNT_IDS.number}`,
          `#${CARD_MOUNT_IDS.date}`,
          `#${CARD_MOUNT_IDS.cvv}`,
          `#${CARD_MOUNT_IDS.postal}`,
        ];
        for (const sel of mountSelectors) {
          if (!document.querySelector(sel)) {
            throw new Error(`Missing mount node ${sel}`);
          }
        }

        const clover = new window.Clover(CLOVER_PAKMS_KEY, {
          merchantId: CLOVER_MERCHANT_ID,
          locale: "en-CA",
        });
        const elements = clover.elements();

        const cardNumber = elements.create("CARD_NUMBER", cloverInputStyles);
        const cardDate = elements.create("CARD_DATE", cloverInputStyles);
        const cardCvv = elements.create("CARD_CVV", cloverInputStyles);
        const cardPostal = elements.create(
          "CARD_POSTAL_CODE",
          cloverInputStyles,
        );

        cardNumber.mount(`#${CARD_MOUNT_IDS.number}`);
        cardDate.mount(`#${CARD_MOUNT_IDS.date}`);
        cardCvv.mount(`#${CARD_MOUNT_IDS.cvv}`);
        cardPostal.mount(`#${CARD_MOUNT_IDS.postal}`);

        if (cancelled) return;
        cloverInstanceRef.current = {
          clover,
          cardNumber,
          cardDate,
          cardCvv,
          cardPostal,
        };
      } catch (err) {
        console.error("Clover init failed:", err);
        const hint =
          process.env.NODE_ENV === "development" && err instanceof Error
            ? ` ${err.message}`
            : "";
        setErrorMessage(
          `Could not initialize the payment form.${hint} If you use live Clover keys, set REACT_APP_CLOVER_USE_PRODUCTION=true in .env.local and restart the dev server.`,
        );
      }
    }

    initClover();

    return () => {
      cancelled = true;
      const instance = cloverInstanceRef.current;
      if (instance) {
        try {
          instance.cardNumber.destroy?.();
          instance.cardDate.destroy?.();
          instance.cardCvv.destroy?.();
          instance.cardPostal.destroy?.();
        } catch (err) {
          console.warn("Clover element teardown failed:", err);
        }
        cloverInstanceRef.current = null;
      }
      // Belt-and-suspenders: empty the mount nodes so a stale iframe can't
      // sit behind a freshly mounted one if destroy() ever no-ops.
      Object.values(CARD_MOUNT_IDS).forEach((id) => {
        const node = document.getElementById(id);
        if (node) node.innerHTML = "";
      });
    };
  }, [open, dialogEntered]);

  // Tokenize the card client-side, then send the token to the cloud function
  // which actually charges the card via Clover and credits the wallet.
  async function handlePay() {
    setErrorMessage("");
    const instance = cloverInstanceRef.current;
    if (!instance) {
      setErrorMessage("Payment form is not ready yet.");
      return;
    }
    setProcessing(true);

    try {
      const result = await instance.clover.createToken();
      if (result?.errors && result.errors.length > 0) {
        const message = result.errors
          .map((e) => e.message || e.code)
          .filter(Boolean)
          .join(", ");
        setErrorMessage(message || "Please check your card details.");
        return;
      }
      if (!result?.token) {
        setErrorMessage("Could not validate card. Please try again.");
        return;
      }

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sourceId: result.token,
          amount,
          userId,
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        setErrorMessage(data.error || "Payment failed. Please try again.");
        return;
      }

      setCommunityWallet(data.wallet);
      // `balance` is tracked in dollars elsewhere (WalletBalance, etransfer/cash
      // confirms) so set it in dollars here too. Round to cents to avoid the
      // usual floating-point ugliness from Clover's response.
      const newTotalDollars =
        Math.round(((data.wallet?.Total ?? 0) + Number.EPSILON) * 100) / 100;
      setBalance(newTotalDollars);
      onClose();
      // If we entered this flow from the "Place Order" button, place the
      // order automatically now that the wallet has funds.
      if (placeOrderOnConfirm) {
        placeOrderOnConfirm();
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("Unexpected error processing payment.");
    } finally {
      setProcessing(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{
        onEntered: () => setDialogEntered(true),
        onExited: () => setDialogEntered(false),
      }}
    >
      <DialogTitle>Confirm Credit Card Details</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Please confirm the transaction on your credit card.
        </Typography>

        <div style={{ display: "flex", flexDirection: "column", marginTop: 8 }}>
          {[
            { label: "Amount loaded to account", value: amount },
            {
              label:
                CloverCreditCardFlatFee > 0
                  ? `Credit Card Fees - ${CloverCreditCardPercentage}% + $${CloverCreditCardFlatFee.toFixed(
                      2,
                    )}`
                  : `Credit Card Fees - ${CloverCreditCardPercentage}%`,
              value: amountPlusFee - amount,
            },
            {
              label: "Total Charges to Card",
              value: amountPlusFee,
              bold: true,
            },
          ].map((item) => (
            <div
              key={item.label}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: item.bold ? 600 : 400,
                fontFamily: "monospace",
                marginBottom: 4,
              }}
            >
              <span style={{ flexGrow: 1, position: "relative" }}>
                {item.label}
                <span
                  style={{
                    position: "absolute",
                    left: "100%",
                    marginLeft: 4,
                    borderBottom: "1px dotted #000",
                    width: "100%",
                    transform: "translateX(-100%)",
                  }}
                />
              </span>
              <span>${item.value.toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Clover iFrame mount points. Each div is replaced with a
            sandboxed iframe by clover.js that handles the card data. */}
        <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
          <CardField label="Card number" fieldId={CARD_MOUNT_IDS.number} />
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <CardField label="Expiry (MM/YY)" fieldId={CARD_MOUNT_IDS.date} />
            <CardField label="CVV" fieldId={CARD_MOUNT_IDS.cvv} />
          </div>
          <CardField label="Postal code" fieldId={CARD_MOUNT_IDS.postal} />
        </div>

        {errorMessage && (
          <Typography
            variant="body2"
            style={{ marginTop: 12, color: "#d32f2f", fontWeight: 500 }}
          >
            {errorMessage}
          </Typography>
        )}

        {processing && (
          <Typography
            variant="body2"
            style={{ marginTop: 8, fontWeight: 500, color: "blue" }}
          >
            Processing card payment...
          </Typography>
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: "space-between" }}>
        <Button
          onClick={handleBack}
          color="secondary"
          variant="outlined"
          disabled={processing}
        >
          BACK
        </Button>
        <Button
          onClick={handlePay}
          color="primary"
          variant="contained"
          disabled={processing}
        >
          {processing ? "PROCESSING..." : "LOAD MONEY"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Small helper that wraps a Clover iframe mount point with a label and a
// minimal border so it looks like a regular text field.
function CardField({ label, fieldId }) {
  return (
    <div>
      <Typography variant="caption" style={{ color: "#666" }}>
        {label}
      </Typography>
      <div
        id={fieldId}
        style={{
          height: 40,
          padding: "0 8px",
          border: "1px solid #ccc",
          borderRadius: 4,
          background: "#fff",
        }}
      />
    </div>
  );
}
